// The Ministry diorama: table, plinth, garden, halls, room floors, walls, rugs, decals and the
// furnished props (docs/05 §3). Static and non-interactive except for the per-room floor meshes,
// which BoardScene uses for picking and highlight dimming.
import * as THREE from 'three';
import type { LocationId } from '@bcm/rules';
import { BUILDING_X, BUILDING_Z, ENTRANCE_X, HALL, ROOMS, ROOM_BY_ID, type RoomLayout } from './layout';
import type { PropLibrary, PropName, Tint } from './assets';
import { ashlar, carpet, flagstones, glowPool, grass, herringbone, magicCircle, marble, planks, starChart, tiles } from './textures';
import { toonFrom } from './materials';
import { mergeStatic } from './merge';
import { applyGroundAO, bakeGroundAO } from './groundAO';

export const TABLE_Y = -0.6;
const BASE_X = [-28.1, 28.1] as const;
const BASE_Z = [-16.1, 23.6] as const;
export const BASE = { x: BASE_X, z: BASE_Z };
export const BUILDING = { x: BUILDING_X, z: BUILDING_Z };
/** The forecourt outside the main entrance: from the building front to the plinth edge. */
const COURT_Z = [BUILDING_Z[1], BASE_Z[1]] as const;
export const ROOM_FLOOR_Y = 0.035;

const WALL_T = 0.4;
const DOOR_W = 2.6;

interface RoomStyle {
  floor: () => THREE.Texture;
  /** Metres per texture repeat. */
  tile: number;
  rough: number;
  metal?: number;
}

// Floors: each room keeps its colour, but in close tones and large (~1 m) tiles so the floor reads as
// a calm surface. Identity comes from colour, panelling and the centrepiece — not from pattern.
const STYLE: Partial<Record<LocationId, RoomStyle>> = {
  conjuration: { floor: () => tiles('#6b3831', '#60322c', 6, 'conj'), tile: 6.5, rough: 0.6 },
  transmutation: { floor: () => tiles('#a88c4c', '#9a7f44', 6, 'trans'), tile: 6.5, rough: 0.45 },
  evocation: { floor: () => marble('#a9bcd8', '#5c6f96'), tile: 6, rough: 0.28 },
  archive: { floor: () => herringbone('#6b4a2e'), tile: 7, rough: 0.6 },
  arcanium: { floor: () => planks('#7a4a2c', 'arc-floor', [1, 1], 7, 0.08), tile: 7, rough: 0.55 },
  enchantment: { floor: () => tiles('#41746f', '#4a7f79', 6, 'ench', [1, 1], true), tile: 6.5, rough: 0.45 },
  procuration: { floor: () => tiles('#cbb994', '#c1ae88', 6, 'proc'), tile: 6.5, rough: 0.55 },
  divination: { floor: () => tiles('#2c4a42', '#315148', 6, 'div', [1, 1], true), tile: 6.5, rough: 0.4 },
  'mana-conduit': { floor: () => tiles('#7a4468', '#834a70', 6, 'mana'), tile: 6.5, rough: 0.35 },
  'grand-auditor': { floor: () => marble('#d6c7aa', '#8a7a62'), tile: 6, rough: 0.3 },
  'planning-office': { floor: () => herringbone('#80573a'), tile: 7, rough: 0.55 },
  precedence: { floor: () => marble('#6a3036', '#b8904a'), tile: 6, rough: 0.3 },
};
export interface MinistryBuild {
  floors: Map<LocationId, THREE.Mesh>;
  lights: Map<LocationId, THREE.PointLight>;
  /** Everything static; merged into a few batched meshes by `furnish`. */
  statics: THREE.Group;
  furnish(lib: PropLibrary): void;
  /** Bake contact occlusion from the furnished statics into every ground surface (once). */
  shadeGround(renderer: THREE.WebGLRenderer): void;
}

/** Ground surfaces that receive the baked occlusion. */
const groundMats: THREE.Material[] = [];
const ground = <M extends THREE.Material>(m: M): M => (groundMats.push(m), m);
/** The area the occlusion bake covers: the whole plinth. */
export const AO_BOUNDS = { x: BASE_X, z: BASE_Z } as const;

const std = (params: THREE.MeshStandardMaterialParameters) => toonFrom(params);

/** A horizontal plane whose UVs are in world metres / `tile`, so textures run seamlessly across rooms. */
function worldPlane(x0: number, x1: number, z0: number, z1: number, y: number, tile: number, mat: THREE.Material): THREE.Mesh {
  const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0);
  g.rotateX(-Math.PI / 2);
  g.translate((x0 + x1) / 2, y, (z0 + z1) / 2);
  const pos = g.attributes.position!;
  const uv = g.attributes.uv!;
  for (let i = 0; i < pos.count; i++) uv.setXY(i, pos.getX(i) / tile, -pos.getZ(i) / tile);
  const m = new THREE.Mesh(g, mat);
  m.receiveShadow = true;
  return m;
}

function boxMesh(w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number, shadow = true, geo?: THREE.BufferGeometry): THREE.Mesh {
  const m = new THREE.Mesh(geo ?? new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = shadow;
  m.receiveShadow = true;
  return m;
}

export function buildMinistry(scene: THREE.Object3D): MinistryBuild {
  const floors = new Map<LocationId, THREE.Mesh>();
  const lights = new Map<LocationId, THREE.PointLight>();
  const statics = new THREE.Group();
  statics.name = 'statics';
  scene.add(statics);
  const root: THREE.Object3D = statics;

  // ─── Table and plinth ──────────────────────────────────────────────────────
  // A dark, quiet walnut: long boards, barely-there grain (the table should never compete).
  const table = worldPlane(-140, 140, -100, 100, TABLE_Y, 16, std({ map: planks('#3a2418', 'table', [1, 1], 4, 0.025) }));
  root.add(table);

  const bw = BASE.x[1] - BASE.x[0];
  const bd = BASE.z[1] - BASE.z[0];
  const bcx = (BASE.x[0] + BASE.x[1]) / 2;
  const bcz = (BASE.z[0] + BASE.z[1]) / 2;
  const plinthMat = std({ color: '#2a1a12', roughness: 0.45, metalness: 0.05 });
  const gold = std({ color: '#c9a14e', roughness: 0.3, metalness: 0.95 });
  root.add(boxMesh(bw + 0.8, 0.16, bd + 0.8, plinthMat, bcx, TABLE_Y + 0.08, bcz));
  // The body stops 1 cm under the grass (its top edge is behind the gilt trim): coplanar, the two
  // would z-fight, and which one showed would depend on draw order.
  root.add(boxMesh(bw, -TABLE_Y - 0.11, bd, plinthMat, bcx, TABLE_Y / 2 - 0.055 + 0.1, bcz));
  for (const [w, d, x, z] of [
    [bw + 0.12, 0.1, bcx, BASE.z[0]],
    [bw + 0.12, 0.1, bcx, BASE.z[1]],
    [0.1, bd + 0.12, BASE.x[0], bcz],
    [0.1, bd + 0.12, BASE.x[1], bcz],
  ] as const) {
    root.add(boxMesh(w, 0.08, d, gold, x, -0.02, z, false));
    root.add(boxMesh(w + 0.8 * Math.sign(w - 1), 0.05, d + 0.8 * Math.sign(d - 1), gold, x, TABLE_Y + 0.17, z, false));
  }

  // ─── Grounds: grass, hall flagstones, the lobby plaza ─────────────────────
  groundMats.length = 0;
  root.add(worldPlane(BASE.x[0], BASE.x[1], BASE.z[0], BASE.z[1], 0.0, 6, ground(std({ map: grass(), roughness: 0.95 }))));
  const hallMat = ground(std({ map: flagstones('#9d9383'), roughness: 0.85 }));
  root.add(worldPlane(BUILDING.x[0], BUILDING.x[1], BUILDING.z[0], BUILDING.z[1], 0.012, 6, hallMat));
  // The entrance hall, where idle Assistants wait on their benches: a diamond-paved lobby across the
  // whole front of the building.
  const plazaMat = ground(std({ map: tiles('#c2b69d', '#b5a98f', 6, 'plaza', [1, 1], true), roughness: 0.85 }));
  root.add(worldPlane(BUILDING.x[0], BUILDING.x[1], HALL.front[0], BUILDING.z[1], 0.02, 3.2, plazaMat));
  // Outside: two broad steps down from the main entrance and a paved path to the front edge.
  const kerb = std({ map: ashlar('#8d8479') });
  const pathMat = ground(std({ map: flagstones('#b3a78f'), roughness: 0.85 }));
  root.add(stoneBox(7.2, 0.1, 0.5, kerb, ENTRANCE_X, 0.05, COURT_Z[0] + 0.55));
  root.add(stoneBox(8.2, 0.06, 0.5, kerb, ENTRANCE_X, 0.03, COURT_Z[0] + 1.0));
  root.add(worldPlane(ENTRANCE_X - 3.2, ENTRANCE_X + 3.2, COURT_Z[0] + 1.25, COURT_Z[1] - 0.05, 0.012, 6, pathMat));

  // ─── Room floors ──────────────────────────────────────────────────────────
  for (const r of ROOMS) {
    const st = STYLE[r.id]!;
    const mat = ground(std({ map: st.floor(), roughness: st.rough, metalness: st.metal ?? 0 }));
    const f = worldPlane(r.x[0] + WALL_T / 2, r.x[1] - WALL_T / 2, r.z[0] + WALL_T / 2, r.z[1] - WALL_T / 2, ROOM_FLOOR_Y, st.tile, mat);
    f.userData.location = r.id;
    scene.add(f); // not batched: dimmed per room and used for picking
    floors.set(r.id, f);
  }

  buildWalls(root);
  buildRoomDress(root);
  buildDecor(root);

  // Accent point lights for the rooms whose centrepiece glows (no shadows: cheap on iGPUs).
  const lightAt = (id: LocationId, color: string, x: number, y: number, z: number, intensity: number, dist: number) => {
    const l = new THREE.PointLight(color, intensity, dist, 1.7);
    l.position.set(x, y, z);
    scene.add(l);
    lights.set(id, l);
  };
  lightAt('conjuration', '#ff6a3c', CIRCLE.x, 2.2, CIRCLE.z, 12, 10);
  lightAt('evocation', '#6aa8ff', PYLON.x, 4.2, PYLON.z, 14, 10);
  lightAt('mana-conduit', '#ff7ac8', FOUNTAIN.x, 2.2, FOUNTAIN.z, 8, 7);

  return {
    floors,
    lights,
    statics,
    furnish: (lib) => {
      furnish(statics, lib);
      const { palettes } = mergeStatic(statics, { paletteGroup: (m) => (groundMats.includes(m) ? 'ground' : 'plain') });
      // The ground's palette material takes the baked occlusion like every other ground surface.
      for (const [key, m] of palettes) if (key.startsWith('ground|')) groundMats.push(m);
    },
    shadeGround: (renderer) => {
      // Only solid things cast contact shadow; not the ground layers themselves (paving, rugs, floor
      // borders) nor the additive decals and light pools lying on them.
      const ao = bakeGroundAO(renderer, statics, AO_BOUNDS, (m) => !m.userData.dynamic && !groundMats.includes(m.material as THREE.Material));
      for (const m of groundMats) applyGroundAO(m, ao, AO_BOUNDS);
    },
  };
}

// ─── Walls ───────────────────────────────────────────────────────────────────

/** A box whose UVs are world metres / `tile` on every face, so masonry textures never stretch. */
function stoneBox(w: number, h: number, d: number, mat: THREE.Material, x: number, y: number, z: number, tile = 2.4, shadow = true): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  const pos = geo.attributes.position!;
  const nor = geo.attributes.normal!;
  const uv = geo.attributes.uv!;
  for (let i = 0; i < pos.count; i++) {
    const px = pos.getX(i) + x;
    const py = pos.getY(i) + y;
    const pz = pos.getZ(i) + z;
    const nx = Math.abs(nor.getX(i));
    const ny = Math.abs(nor.getY(i));
    if (ny > 0.5) uv.setXY(i, px / tile, pz / tile);
    else if (nx > 0.5) uv.setXY(i, pz / tile, py / tile);
    else uv.setXY(i, px / tile, py / tile);
  }
  return boxMesh(w, h, d, mat, x, y, z, shadow, geo);
}

/** Union every room edge per line, cut the doors out, then build each run once (no z-fighting). */
function buildWalls(root: THREE.Object3D): void {
  type Run = [number, number];
  const lines = new Map<string, { axis: 'x' | 'z'; at: number; runs: Run[]; doors: Run[] }>();
  const line = (axis: 'x' | 'z', at: number) => {
    const key = `${axis}:${at.toFixed(2)}`;
    let l = lines.get(key);
    if (!l) {
      l = { axis, at, runs: [], doors: [] };
      lines.set(key, l);
    }
    return l;
  };
  for (const r of ROOMS) {
    if (r.open) continue; // an open area of a hall: no walls of its own
    line('z', r.z[0]).runs.push([r.x[0], r.x[1]]);
    line('z', r.z[1]).runs.push([r.x[0], r.x[1]]);
    line('x', r.x[0]).runs.push([r.z[0], r.z[1]]);
    line('x', r.x[1]).runs.push([r.z[0], r.z[1]]);
    const d = r.door;
    if (d.side === 'n' || d.side === 's') line('z', d.side === 'n' ? r.z[0] : r.z[1]).doors.push([d.x - DOOR_W / 2, d.x + DOOR_W / 2]);
    else line('x', d.side === 'w' ? r.x[0] : r.x[1]).doors.push([d.z - DOOR_W / 2, d.z + DOOR_W / 2]);
  }
  // The outer shell: full-length side walls (closing the hall ends) and the front with the main entrance.
  line('x', BUILDING.x[0]).runs.push([BUILDING.z[0], BUILDING.z[1]]);
  line('x', BUILDING.x[1]).runs.push([BUILDING.z[0], BUILDING.z[1]]);
  const front = line('z', BUILDING.z[1]);
  front.runs.push([BUILDING.x[0], BUILDING.x[1]]);
  front.doors.push([ENTRANCE_X - DOOR_W / 2, ENTRANCE_X + DOOR_W / 2]);

  const stone = std({ map: ashlar('#a39a8d') });
  const stoneDark = std({ map: ashlar('#857c72') });
  const base = std({ map: ashlar('#6f675e') });
  const coping = std({ map: ashlar('#968a78') });
  const trim = std({ color: '#6e6258' });
  const gold = std({ color: '#c9a14e', metalness: 1 });
  const threshold = std({ color: '#7d6848' });
  const lead = std({ color: '#241a14' });
  // Stained glass: jewel colours, lit only faintly — windows must never outshine the rooms.
  const panes = ['#2f4f7a', '#6a2f3f', '#2f5a4a', '#5a4a2a'].map((c) => std({ color: c, emissive: c, emissiveIntensity: 0.12 }));

  const isOuter = (l: { axis: 'x' | 'z'; at: number }) =>
    (l.axis === 'z' && Math.abs(l.at - BUILDING.z[0]) < 0.01) || (l.axis === 'x' && (Math.abs(l.at - BUILDING.x[0]) < 0.01 || Math.abs(l.at - BUILDING.x[1]) < 0.01));

  // Pilasters are shared where runs meet: one per spot (runs can meet a few centimetres apart, and
  // two pilasters that close would overlap and z-fight).
  const pilasters: { x: number; z: number; h: number; t: number; big: boolean }[] = [];
  const pilaster = (x: number, z: number, h: number, t: number, big: boolean) => {
    const cur = pilasters.find((p) => Math.abs(p.x - x) < 0.6 && Math.abs(p.z - z) < 0.6);
    if (!cur) return void pilasters.push({ x, z, h, t, big });
    if (h > cur.h || (big && !cur.big)) Object.assign(cur, { x, z });
    Object.assign(cur, { h: Math.max(h, cur.h), t: Math.max(t, cur.t), big: big || cur.big });
  };

  for (const l of lines.values()) {
    const cut = subtract(union(l.runs), l.doors);
    const back = l.axis === 'z' && l.at <= BUILDING.z[0] + 0.01;
    const front = l.axis === 'z' && l.at >= BUILDING.z[1] - 0.01;
    const outer = isOuter(l);
    const h = back ? 3.6 : front ? 1.35 : outer ? 1.6 : 1.25;
    const t = outer ? WALL_T * 1.6 : front ? WALL_T * 1.3 : WALL_T;
    const at = (p: number): [number, number] => (l.axis === 'z' ? [p, l.at] : [l.at, p]);
    const box3 = (len: number, hh: number, tt: number): [number, number] => (l.axis === 'z' ? [len, tt] : [tt, len]);
    for (const [a, b] of cut) {
      const len = b - a;
      if (len < 0.1) continue;
      const mid = (a + b) / 2;
      const [x, z] = at(mid);
      // Masonry in three courses: a dark plinth, the ashlar body and a pale coping with an overhang.
      const [bw, bd] = box3(len + t + 0.1, 0, t + 0.12);
      root.add(stoneBox(bw, 0.26, bd, base, x, 0.13, z));
      const [ww, wd] = box3(len + t, 0, t);
      root.add(stoneBox(ww, h - 0.26, wd, outer ? stoneDark : stone, x, 0.26 + (h - 0.26) / 2, z));
      // Copings of crossing walls overlap at every junction: each stops 2 mm short, and those
      // running north–south sit 1 mm lower, so no two share a face there (they z-fought).
      const [cw, cd] = box3(len + t + 0.16 - 0.004, 0, t + 0.16);
      root.add(boxMesh(cw, 0.12, cd, coping, x, h + 0.06 - (l.axis === 'x' ? 0.001 : 0), z));
      if (!back) {
        // Pilasters at the ends and every ~3.4 m, proud of the wall on both faces.
        const n = Math.max(1, Math.round(len / 3.4));
        for (let i = 0; i <= n; i++) {
          const [px, pz] = at(a + (len * i) / n);
          pilaster(px, pz, h, t, i === 0 || i === n);
        }
      }
      if (outer && !back) {
        // Battlements along the outer side walls.
        const n = Math.floor(len / 1.1);
        for (let i = 0; i <= n; i++) {
          const [mx, mz] = at(a + (len * i) / Math.max(1, n));
          root.add(stoneBox(l.axis === 'z' ? 0.55 : t, 0.42, l.axis === 'z' ? t : 0.55, stoneDark, mx, h + 0.33, mz));
        }
      }
      if (back) {
        const inner = l.at + t / 2;
        const n = Math.floor(len / 3.4);
        for (let i = 0; i <= n; i++) {
          const px = a + (len * i) / Math.max(1, n);
          root.add(stoneBox(0.5, h + 0.2, 0.34, stoneDark, px, (h + 0.2) / 2, inner + 0.12));
          root.add(boxMesh(0.62, 0.16, 0.44, gold, px, h - 0.5, inner + 0.14, false));
        }
        for (let i = 0; i < n; i++) {
          const wx = a + (len * (i + 0.5)) / Math.max(1, n);
          root.add(archWindow(wx, 1.2, inner + 0.02, 1.1, 1.8, lead, panes[(((i + Math.round(a)) % panes.length) + panes.length) % panes.length]!, trim));
        }
        // Battlements along the top of the back wall.
        const m = Math.floor(len / 1.1);
        for (let i = 0; i <= m; i++) root.add(stoneBox(0.55, 0.42, t, stoneDark, a + (len * i) / Math.max(1, m), h + 0.33, l.at));
      }
    }
    // Doorways: gateposts carrying a flat stone arch with a gilded keystone, and a brass threshold.
    for (const [a, b] of l.doors) {
      const mid = (a + b) / 2;
      const [x, z] = at(mid);
      const [tw, td] = box3(b - a, 0, t);
      root.add(boxMesh(tw, 0.03, td, threshold, x, ROOM_FLOOR_Y + 0.01, z, false));
      const ph = h + 0.1;
      for (const e of [a, b]) {
        const [px, pz] = at(e);
        root.add(stoneBox(0.56, 0.3, 0.56, base, px, 0.15, pz));
        root.add(stoneBox(0.48, ph, 0.48, stoneDark, px, ph / 2, pz));
        root.add(boxMesh(0.62, 0.12, 0.62, coping, px, ph + 0.06, pz));
      }
      root.add(doorArch(x, ph + 0.12, z, b - a, t + 0.08, l.axis, stoneDark, gold));
    }
  }
  for (const pl of pilasters) {
    // A few millimetres proud of whatever they would otherwise share a face with (z-fighting):
    // plain ones of the coping (wall + 16 cm), big ones of the gatepost caps and base courses.
    const w = pl.big ? 0.626 : 0.5;
    const d = pl.t + (pl.big ? 0.226 : 0.164);
    root.add(stoneBox(w + 0.08, 0.3, d + 0.08, base, pl.x, 0.15, pl.z));
    root.add(stoneBox(w, pl.h + 0.18, d, stoneDark, pl.x, (pl.h + 0.18) / 2, pl.z));
    root.add(boxMesh(w + 0.144, 0.1, d + 0.144, coping, pl.x, pl.h + 0.23, pl.z)); // 2 mm proud of the battlements
    if (pl.big) {
      const fin = new THREE.Mesh(new THREE.OctahedronGeometry(0.15, 0), gold);
      fin.position.set(pl.x, pl.h + 0.43, pl.z);
      fin.scale.y = 1.6;
      root.add(fin);
    }
  }
  buildTowers(root, stoneDark, trim, gold);
}

/** A shallow (elliptical) stone arch over a doorway, with voussoir joints hinted by a gilded keystone. */
function doorArch(x: number, y: number, z: number, span: number, depth: number, axis: 'x' | 'z', stoneMat: THREE.Material, gold: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const rx = span / 2 + 0.24;
  const rise = 0.62;
  const shape = new THREE.Shape();
  shape.moveTo(-rx, 0);
  shape.absellipse(0, 0, rx, rise + 0.3, Math.PI, 0, true);
  shape.lineTo(rx, 0);
  shape.lineTo(span / 2, 0);
  shape.absellipse(0, 0, span / 2, rise, 0, Math.PI, false);
  shape.lineTo(-rx, 0);
  const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 14 });
  geo.translate(0, 0, -depth / 2);
  const arch = new THREE.Mesh(geo, stoneMat);
  arch.castShadow = true;
  arch.receiveShadow = true;
  const key = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.36, depth + 0.04), gold);
  key.position.set(0, rise + 0.14, 0);
  g.add(arch, key);
  g.position.set(x, y, z);
  if (axis === 'x') g.rotation.y = Math.PI / 2;
  return g;
}

/**
 * Each room's own face: waist-high panelling in the room's colour with a brass rail along every
 * inner wall (door gaps left open), and a border inlay framing its floor.
 */
function buildRoomDress(root: THREE.Object3D): void {
  const inset = WALL_T / 2;
  for (const r of ROOMS) {
    if (r.open) continue;
    const panel = std({ color: panelColor(r.accent) });
    const rail = std({ color: '#8a6d3c' });
    const border = ground(std({ color: new THREE.Color(r.tint).multiplyScalar(0.55).getStyle() }));
    const sides: { axis: 'x' | 'z'; at: number; from: number; to: number; into: number; side: RoomLayout['door']['side'] }[] = [
      { axis: 'z', at: r.z[0] + inset, from: r.x[0] + inset, to: r.x[1] - inset, into: 1, side: 'n' },
      { axis: 'z', at: r.z[1] - inset, from: r.x[0] + inset, to: r.x[1] - inset, into: -1, side: 's' },
      { axis: 'x', at: r.x[0] + inset, from: r.z[0] + inset, to: r.z[1] - inset, into: 1, side: 'w' },
      { axis: 'x', at: r.x[1] - inset, from: r.z[0] + inset, to: r.z[1] - inset, into: -1, side: 'e' },
    ];
    for (const sd of sides) {
      const door = r.door.side === sd.side ? (sd.axis === 'z' ? r.door.x : r.door.z) : null;
      const runs: [number, number][] = door === null ? [[sd.from, sd.to]] : subtract([[sd.from, sd.to]], [[door - DOOR_W / 2 - 0.3, door + DOOR_W / 2 + 0.3]]);
      for (const [a, b] of runs) {
        const len = b - a;
        if (len < 0.3) continue;
        const mid = (a + b) / 2;
        // 7 cm deep: 1 cm proud of the wall's base course, whose face it used to share (z-fighting).
        const off = sd.at + sd.into * 0.035;
        const [x, z, w, d] = sd.axis === 'z' ? [mid, off, len, 0.07] : [off, mid, 0.07, len];
        root.add(boxMesh(w, 0.78, d, panel, x, 0.39 + ROOM_FLOOR_Y, z, false));
        const [rx, rz, rw, rd] = sd.axis === 'z' ? [mid, sd.at + sd.into * 0.06, len, 0.08] : [sd.at + sd.into * 0.06, mid, 0.08, len];
        root.add(boxMesh(rw, 0.06, rd, rail, rx, 0.8 + ROOM_FLOOR_Y, rz, false));
      }
      // Floor border: a darker band just inside the panelling.
      const bw = 0.32;
      const bo = sd.at + sd.into * (0.07 + bw / 2);
      const [x, z, w, d] = sd.axis === 'z' ? [(sd.from + sd.to) / 2, bo, sd.to - sd.from - 0.14, bw] : [bo, (sd.from + sd.to) / 2, bw, sd.to - sd.from - 0.14];
      const band = new THREE.Mesh(new THREE.PlaneGeometry(w, d), border);
      band.rotation.x = -Math.PI / 2;
      band.position.set(x, ROOM_FLOOR_Y + 0.004, z);
      band.receiveShadow = true;
      root.add(band);
    }
  }
}

/** Panelling colour: the room accent, deepened and warmed toward wood. */
function panelColor(accent: string): string {
  return new THREE.Color(accent).lerp(new THREE.Color('#3a2616'), 0.62).getStyle();
}

/** A lancet window: stone surround, lead cames and jewel glass. */
function archWindow(x: number, y: number, z: number, w: number, h: number, lead: THREE.Material, glass: THREE.Material, surround: THREE.Material): THREE.Group {
  const g = new THREE.Group();
  const shape = new THREE.Shape();
  shape.moveTo(-w / 2, 0);
  shape.lineTo(-w / 2, h - w / 2);
  shape.absarc(0, h - w / 2, w / 2, Math.PI, 0, true);
  shape.lineTo(w / 2, 0);
  shape.closePath();
  const outer = new THREE.Shape();
  const W = w + 0.24;
  outer.moveTo(-W / 2, -0.12);
  outer.lineTo(-W / 2, h - w / 2);
  outer.absarc(0, h - w / 2, W / 2, Math.PI, 0, true);
  outer.lineTo(W / 2, -0.12);
  outer.closePath();
  outer.holes.push(shape);
  const frame = new THREE.Mesh(new THREE.ExtrudeGeometry(outer, { depth: 0.1, bevelEnabled: false, curveSegments: 12 }), surround);
  const pane = new THREE.Mesh(new THREE.ShapeGeometry(shape, 12), glass);
  pane.position.z = 0.02;
  g.add(frame, pane);
  // Lead cames: a mullion, a transom and a diamond lattice hint.
  for (const [cw, ch, cx, cy] of [
    [0.05, h, 0, h / 2],
    [w, 0.05, 0, h * 0.42],
    [w, 0.05, 0, h * 0.72],
  ] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(cw, ch, 0.04), lead);
    bar.position.set(cx, cy, 0.05);
    g.add(bar);
  }
  g.position.set(x, y, z);
  return g;
}

/** Corner turrets with slate spires (the Ministry's silhouette). */
function buildTowers(root: THREE.Object3D, stone: THREE.Material, trim: THREE.Material, gold: THREE.Material): void {
  const slate = std({ color: '#3a3f5c' });
  const spots: [number, number, number][] = [
    [BUILDING.x[0], BUILDING.z[0], 5.4],
    [BUILDING.x[1], BUILDING.z[0], 5.4],
    [BUILDING.x[0], BUILDING.z[1], 3.2],
    [BUILDING.x[1], BUILDING.z[1], 3.2],
  ];
  for (const [x, z, h] of spots) {
    const r = h > 4 ? 1.55 : 1.05;
    const body = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.08, h, 20), stone);
    body.position.set(x, h / 2, z);
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.18, r * 1.18, 0.3, 20), trim);
    ring.position.set(x, h + 0.15, z);
    const roof = new THREE.Mesh(new THREE.ConeGeometry(r * 1.3, h > 4 ? 3.4 : 2.2, 20), slate);
    roof.position.set(x, h + 0.3 + (h > 4 ? 1.7 : 1.1), z);
    const finial = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 8), gold);
    finial.position.set(x, h + 0.3 + (h > 4 ? 3.5 : 2.3), z);
    for (const m of [body, ring, roof, finial]) {
      m.castShadow = true;
      m.receiveShadow = true;
      root.add(m);
    }
    // Arrow-slit windows.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const slit = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.7, 0.1), std({ color: '#140e0a' }));
      slit.position.set(x + Math.cos(a) * (r + 0.01), h * 0.62, z + Math.sin(a) * (r + 0.01));
      slit.rotation.y = -a + Math.PI / 2;
      root.add(slit);
    }
  }
}

function union(runs: [number, number][]): [number, number][] {
  const s = [...runs].sort((p, q) => p[0] - q[0]);
  const out: [number, number][] = [];
  for (const [a, b] of s) {
    const last = out[out.length - 1];
    if (last && a <= last[1] + 1e-3) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

function subtract(runs: [number, number][], holes: [number, number][]): [number, number][] {
  let out = runs;
  for (const [ha, hb] of holes) {
    out = out.flatMap(([a, b]) => {
      if (hb <= a || ha >= b) return [[a, b] as [number, number]];
      const parts: [number, number][] = [];
      if (ha > a) parts.push([a, ha]);
      if (hb < b) parts.push([hb, b]);
      return parts;
    });
  }
  return out;
}

// ─── Rugs and glowing decals (no props needed) ───────────────────────────────

function buildDecor(root: THREE.Object3D): void {
  const rug = (field: string, border: string, w: number, d: number, x: number, z: number, key: string) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, d), ground(std({ map: carpet(field, border, w, d, key), roughness: 0.95 })));
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, ROOM_FLOOR_Y + 0.012, z);
    m.receiveShadow = true;
    root.add(m);
  };
  rug('#3b2352', '#b58a3a', 6.4, 4.2, -18.5, 5.8, 'arc');
  rug('#23452f', '#a8844a', 5.2, 2.6, 18.4, -9.0, 'arch');
  rug('#7a1f2b', '#d0a24a', 2.2, 9.6, 19.75, 7.6, 'aud');
  rug('#27405e', '#b58a3a', 4.8, 2.4, -6.0, 11.2, 'plan-a');
  rug('#27405e', '#b58a3a', 4.8, 2.4, 8.6, 11.2, 'plan-b');
  // A runner down the entrance hall, from the main entrance to the Planning Office door.
  rug('#5a1f2a', '#c9a14e', 2.4, HALL.front[1] - HALL.front[0] - 0.5, (ENTRANCE_X + 1.25) / 2, (HALL.front[0] + HALL.front[1]) / 2, 'hall');

  const decal = (tex: THREE.Texture, size: number, x: number, z: number, color: string, opacity = 0.85) => {
    const mat = new THREE.MeshBasicMaterial({ map: tex, color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, ROOM_FLOOR_Y + 0.02, z);
    m.userData.dynamic = true; // additive decal: keep out of the opaque batches
    root.add(m);
    return m;
  };
  decal(magicCircle('#ffffff', 'penta', 5), 5.4, CIRCLE.x, CIRCLE.z, '#c8573c', 0.5);
  decal(magicCircle('#ffffff', 'hexa', 6), 3.0, PYLON.x, PYLON.z, '#5a86c8', 0.4);
  decal(starChart('#ffffff'), 3.6, 7, 0.8, '#7ac8a8', 0.35);

  // The Office of Precedence: a brass rail on posts closes it off from the entrance hall, open
  // opposite its desk.
  const pr = ROOM_BY_ID.get('precedence')!;
  const brass = std({ color: '#c9a14e', metalness: 0.9, roughness: 0.3 });
  const x = pr.x[0] + 0.15;
  const gap = [pr.door.z - 1.3, pr.door.z + 1.3] as const;
  for (const [a, b] of [
    [pr.z[0] + 0.35, gap[0]],
    [gap[1], pr.z[1] - 0.4],
  ] as const) {
    const len = b - a;
    root.add(boxMesh(0.09, 0.07, len, brass, x, 0.92, (a + b) / 2, false));
    root.add(boxMesh(0.05, 0.05, len, brass, x, 0.5, (a + b) / 2, false));
    const n = Math.max(1, Math.round(len / 1.1));
    for (let i = 0; i <= n; i++) {
      const z = a + (len * i) / n;
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.92, 10), brass);
      post.position.set(x, 0.46, z);
      root.add(post);
      if (i === 0 || i === n) {
        const knob = new THREE.Mesh(new THREE.SphereGeometry(0.1, 12, 8), brass);
        knob.position.set(x, 1.0, z);
        root.add(knob);
      }
    }
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Placement {
  p: PropName;
  x: number;
  z: number;
  rot?: number;
  s?: number;
  tint?: Tint;
  y?: number;
}

const RED = { accent: '#8e2231', glow: '#ff5a3c' };
const YELLOW = { accent: '#9a7620', liquid: '#ffcf4a', glow: '#ffcf4a' };
const BLUE = { accent: '#284c8e', glow: '#6aa8ff' };
const GREEN = { accent: '#2c5e3c', glow: '#6fe0a0' };
const PINK = { glow: '#f26bbd', liquid: '#f58ad0' };

// Room centrepieces (also used for their lights and floor decals).
const CIRCLE = { x: -16.5, z: -9.8 };
const PYLON = { x: 7, z: -10.2 };
const FOUNTAIN = { x: 7, z: 5.3 };

/** Inner wall faces of a room (past the panelling), for placing furniture against walls. */
function faces(id: LocationId) {
  const r = ROOM_BY_ID.get(id)!;
  const outerBack = Math.abs(r.z[0] - BUILDING.z[0]) < 0.01;
  return { n: r.z[0] + (outerBack ? 0.32 + 0.1 : 0.2 + 0.08), s: r.z[1] - 0.28, w: r.x[0] + 0.28, e: r.x[1] - 0.28, cx: (r.x[0] + r.x[1]) / 2 };
}

/**
 * Furnishing, room by room (layout v3). Every room has a hero centrepiece in its colour plus
 * dressing; the Assistant stations (layout.ts) and the path from each door to them stay clear.
 */
function placements(): Placement[] {
  const cj = faces('conjuration');
  const tr = faces('transmutation');
  const ev = faces('evocation');
  const ar = faces('archive');
  const ac = faces('arcanium');
  const en = faces('enchantment');
  const pr = faces('procuration');
  const dv = faces('divination');
  const mc = faces('mana-conduit');
  const au = faces('grand-auditor');
  const po = faces('planning-office');
  return [
    // Conjuration: the summoning circle, braziers, a cauldron and candles.
    { p: 'summoning', x: CIRCLE.x, z: CIRCLE.z, tint: RED },
    { p: 'bookshelf', x: cj.w + 1.2, z: cj.n + 0.25 },
    { p: 'bookshelf', x: cj.e - 1.2, z: cj.n + 0.25 },
    { p: 'banner', x: -20.5, z: cj.n + 0.05, y: 0.75, tint: RED },
    { p: 'banner', x: -12.5, z: cj.n + 0.05, y: 0.75, tint: RED },
    { p: 'brazier', x: cj.w + 1.0, z: -7.8, tint: RED },
    { p: 'brazier', x: cj.e - 1.0, z: -7.8, tint: RED },
    { p: 'cauldron', x: cj.w + 1.3, z: cj.s - 0.9, tint: { liquid: '#7fe07a' } },
    { p: 'candles', x: -13.3, z: cj.n + 0.9 },
    { p: 'candles', x: -19.9, z: -8.0 },
    { p: 'lectern', x: cj.e - 1.6, z: -10.4, rot: -35 },
    { p: 'book_pile', x: cj.e - 0.6, z: -9.2 },
    { p: 'plant', x: cj.e - 0.4, z: cj.s - 0.4 },
    // Transmutation: the alchemy bench, an athanor, barrels and crates.
    { p: 'alchemy', x: -2.6, z: tr.n + 0.55, tint: YELLOW },
    { p: 'furnace', x: tr.w + 0.85, z: tr.n + 0.7 },
    { p: 'bookshelf_low', x: tr.e - 0.9, z: tr.n + 0.22 },
    { p: 'banner', x: -2.6, z: tr.n + 0.05, y: 0.9, tint: YELLOW },
    { p: 'barrels', x: tr.e - 0.55, z: -8.6 },
    { p: 'filing_cabinet', x: tr.w + 0.35, z: -8.0, rot: 90 },
    { p: 'crates', x: tr.e - 0.6, z: tr.s - 0.7 },
    { p: 'candles', x: tr.w + 0.6, z: tr.s - 0.6 },
    // Evocation: the pylon between tesla coils and blue crystals.
    { p: 'pylon', x: PYLON.x, z: PYLON.z, tint: BLUE },
    // On the side walls: the back wall's buttresses leave no room for them there.
    { p: 'bookshelf_low', x: ev.w + 0.25, z: -10.6, rot: 90 },
    { p: 'bookshelf_low', x: ev.e - 0.25, z: -10.6, rot: -90 },
    { p: 'banner', x: 4.3, z: ev.n + 0.05, y: 0.75, tint: BLUE },
    { p: 'banner', x: 9.7, z: ev.n + 0.05, y: 0.75, tint: BLUE },
    { p: 'tesla', x: ev.w + 0.6, z: -8.4, tint: BLUE },
    { p: 'tesla', x: ev.e - 0.6, z: -8.4, tint: BLUE },
    { p: 'crystals', x: ev.w + 0.7, z: ev.s - 0.8, tint: BLUE },
    { p: 'crystals', x: ev.e - 0.7, z: ev.s - 0.8, tint: BLUE, rot: 70 },
    // Archive: the stacks, a reading table, the card catalogue, a globe and piles of books.
    { p: 'stacks', x: 18.4, z: ar.n + 0.3 },
    { p: 'meeting_table', x: 18.4, z: -9.1, s: 0.95 },
    { p: 'chair', x: 17.3, z: -10.1 },
    { p: 'chair', x: 19.5, z: -10.1 },
    { p: 'card_catalog', x: ar.e - 0.3, z: -8.4, rot: -90 },
    { p: 'filing_cabinet_tall', x: ar.w + 0.35, z: -8.6, rot: 90 },
    { p: 'globe', x: ar.w + 0.8, z: ar.s - 0.8 },
    { p: 'book_pile', x: ar.e - 0.7, z: ar.s - 0.7, rot: 30 },
    { p: 'book_pile', x: 14.2, z: -10.9, rot: -20 },
    // Arcanium: the shop, display cases, barrels and crates.
    { p: 'shop', x: -18.5, z: ac.n + 1.2, tint: { accent: '#6b3a8a' } },
    { p: 'display_case', x: ac.w + 0.45, z: 3.8, rot: 90 },
    { p: 'display_case', x: ac.w + 0.45, z: 8.2, rot: 90 },
    { p: 'bookshelf_low', x: ac.w + 0.25, z: 12.2, rot: 90 },
    { p: 'barrels', x: ac.e - 0.7, z: 1.2 },
    { p: 'crates', x: ac.e - 0.7, z: ac.s - 0.7 },
    { p: 'candelabra', x: ac.w + 0.5, z: 0.6 },
    { p: 'plant', x: ac.w + 0.4, z: ac.s - 0.4 },
    // Enchantment: typing desks lit by rune lanterns.
    // (Centred at −3.0, not the room's −2.55, so the east desk clears the panelling.)
    { p: 'enchant_desks', x: -3.0, z: -0.35, tint: { glow: '#4fd2c6' } },
    { p: 'chair', x: -6.4, z: 0.55, rot: 180 },
    { p: 'chair', x: -3.0, z: 0.55, rot: 180 },
    { p: 'chair', x: 0.4, z: 0.55, rot: 180 },
    { p: 'rune_lantern', x: en.w + 0.4, z: en.s - 0.4, tint: { glow: '#4fd2c6' } },
    { p: 'rune_lantern', x: en.e - 0.4, z: en.s - 0.4, tint: { glow: '#4fd2c6' } },
    { p: 'filing_cabinet', x: en.w + 0.35, z: -0.6 },
    // Procuration: the stamp counter and a wall of pigeonholes.
    { p: 'stamp_counter', x: -4.25, z: pr.n + 0.55 },
    { p: 'pigeonholes', x: pr.e - 0.2, z: 6.3, rot: -90 },
    { p: 'filing_cabinet', x: pr.w + 0.35, z: pr.n + 0.4 },
    { p: 'filing_cabinet_tall', x: pr.e - 0.35, z: pr.n + 0.4 },
    { p: 'plant', x: pr.w + 0.4, z: pr.s - 0.4 },
    // Divination: the crystal ball and telescope, an orrery and green crystals.
    { p: 'crystal_ball', x: 9.5, z: -0.2, tint: { glow: '#9fffd6' } },
    { p: 'orrery', x: 4.3, z: 0.3, tint: { glow: '#ffd98a' } },
    { p: 'bookshelf_low', x: dv.w + 1.0, z: dv.n + 0.22 },
    { p: 'crystals', x: dv.e - 0.6, z: dv.s - 0.6, tint: GREEN },
    { p: 'candelabra', x: dv.w + 0.45, z: dv.s - 0.45 },
    // Mana Conduit: the fountain, wall pipes and pink crystals (mana is pink everywhere).
    { p: 'fountain', x: FOUNTAIN.x, z: FOUNTAIN.z, s: 0.82, tint: PINK },
    { p: 'pipes', x: mc.w + 0.9, z: mc.n + 0.12, tint: PINK },
    { p: 'pipes', x: mc.e - 0.9, z: mc.n + 0.12, tint: PINK },
    { p: 'crystals', x: mc.w + 0.6, z: mc.s - 0.6, tint: PINK },
    { p: 'plant', x: mc.e - 0.4, z: mc.s - 0.4 },
    // Grand Auditor: the bench on its dais, a colonnade and a red carpet.
    { p: 'judge_bench', x: 19.75, z: au.n + 1.4, tint: { accent: '#8e2231' } },
    { p: 'column', x: 16.1, z: 4.0 },
    { p: 'column', x: 23.4, z: 4.0 },
    { p: 'column', x: 16.1, z: 12.6 },
    { p: 'column', x: 23.4, z: 12.6 },
    { p: 'candelabra', x: au.w + 0.5, z: au.n + 0.5 },
    { p: 'candelabra', x: au.e - 0.5, z: au.n + 0.5 },
    { p: 'book_pile', x: au.e - 0.6, z: 8.2 },
    // Office of Precedence: the reception desk, a banner and the records behind it.
    { p: 'desk', x: 21.0, z: 16.3, s: 1.1 },
    { p: 'chair', x: 21.0, z: 15.35 },
    { p: 'banner', x: 21.0, z: 14.05, y: 0.75, tint: { accent: '#c9a14e' } },
    { p: 'filing_cabinet_tall', x: 23.95, z: 14.6, rot: -90 },
    { p: 'filing_cabinet', x: 23.95, z: 15.6, rot: -90 },
    { p: 'candelabra', x: 18.0, z: 14.45 },
    { p: 'plant', x: 23.9, z: 19.9 },
    // Planning Office: the memo board, two map tables, a globe and an easel.
    { p: 'planning_board', x: 1.25, z: po.n + 0.35 },
    { p: 'meeting_table', x: -6.0, z: 11.2 },
    { p: 'meeting_table', x: 8.6, z: 11.2 },
    { p: 'chair', x: -7.1, z: 10.25 },
    { p: 'chair', x: -4.9, z: 10.25 },
    { p: 'chair', x: 7.5, z: 10.25 },
    { p: 'chair', x: 9.7, z: 10.25 },
    { p: 'filing_cabinet', x: po.w + 0.35, z: po.n + 0.4 },
    { p: 'filing_cabinet', x: po.e - 0.35, z: po.n + 0.4 },
    { p: 'globe', x: po.w + 0.6, z: po.s - 0.6 },
    { p: 'easel', x: po.e - 0.8, z: po.s - 0.9, rot: -25 },
    // Halls: planters at the ends and wall lamps along the way.
    { p: 'plant', x: BUILDING.x[0] + 0.7, z: -2.8 },
    { p: 'plant', x: BUILDING.x[1] - 0.7, z: -2.8 },
    { p: 'plant', x: BUILDING.x[0] + 0.8, z: 14.6 },
    { p: 'plant', x: 16.6, z: 14.3 }, // the east end of the hall is the Office of Precedence
    { p: 'plant', x: ENTRANCE_X - 2.2, z: BUILDING.z[1] - 0.8 },
    { p: 'plant', x: ENTRANCE_X + 2.2, z: BUILDING.z[1] - 0.8 },
    ...[-20, -12, 8.5, 20].map((x) => ({ p: 'sconce' as const, x, z: BUILDING.z[1] - 0.52 + 0.28, rot: 180 })),
    ...[-20, -12, 12, 20].map((x) => ({ p: 'sconce' as const, x, z: -1.6 - 0.2 - 0.02, rot: 180 })),
    // The forecourt: statues flanking the steps, lamps along the front.
    { p: 'statue', x: ENTRANCE_X - 5.0, z: COURT_Z[0] + 1.1, s: 0.85 },
    { p: 'statue', x: ENTRANCE_X + 5.0, z: COURT_Z[0] + 1.1, s: 0.85 },
    { p: 'lamp_post', x: -10.3, z: COURT_Z[0] + 1.2 },
    { p: 'lamp_post', x: 10.3, z: COURT_Z[0] + 1.2 },
    { p: 'lamp_post', x: -18.5, z: COURT_Z[0] + 1.2 },
    { p: 'lamp_post', x: 18.5, z: COURT_Z[0] + 1.2 },
  ];
}

function garden(): Placement[] {
  const out: Placement[] = [];
  let seed = 9;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  // Back: a row of broad trees behind the tall wall.
  for (let x = BASE.x[0] + 1.6; x <= BASE.x[1] - 1.5; x += 4.1) {
    out.push({ p: rnd() < 0.5 ? 'tree' : 'tree_small', x: x + (rnd() - 0.5), z: BUILDING.z[0] - 1.7 + (rnd() - 0.5) * 0.6, rot: rnd() * 360 });
  }
  // Sides: cypresses and bushes (kept low toward the front so the table cards stay clear).
  for (const side of [-1, 1]) {
    for (let z = BUILDING.z[0] + 1; z <= BUILDING.z[1] - 1; z += 3.2) {
      const x = side * (BUILDING.x[1] + 1.9 + (rnd() - 0.5) * 0.4);
      out.push(z < 8 ? { p: 'cypress', x, z, s: 0.8 + rnd() * 0.25 } : { p: 'bush', x, z, s: 1 + rnd() * 0.3, rot: rnd() * 360 });
    }
  }
  // Front hedges either side of the entrance path.
  for (const side of [-1, 1]) {
    for (let x = 5.8; x < BASE.x[1] - 2; x += 4.1) out.push({ p: 'hedge', x: side * (x + 2), z: BASE.z[1] - 0.75 });
  }
  out.push({ p: 'bush', x: -3.9, z: BASE.z[1] - 0.75 }, { p: 'bush', x: 3.9, z: BASE.z[1] - 0.75 });
  return out;
}

/**
 * Light pools: every flame, lantern and glowing centrepiece throws a soft pool of its colour onto the
 * floor. They are all one additive mesh (vertex colours), so the whole set costs a single draw call.
 */
const POOLS: Partial<Record<PropName, { color: string; r: number; k: number; dz?: number }>> = {
  brazier: { color: '#ff7a3a', r: 2.4, k: 0.55 },
  candles: { color: '#ffb45a', r: 1.5, k: 0.45 },
  candelabra: { color: '#ffc070', r: 1.9, k: 0.4 },
  sconce: { color: '#ffc070', r: 1.7, k: 0.42, dz: 0.55 },
  lamp_post: { color: '#ffd08a', r: 2.8, k: 0.38 },
  furnace: { color: '#ff7a3a', r: 2.2, k: 0.5, dz: 0.9 },
  rune_lantern: { color: '#4fd2c6', r: 1.9, k: 0.45 },
  crystals: { color: '#ffffff', r: 1.9, k: 0.4 },
  tesla: { color: '#6aa8ff', r: 1.4, k: 0.4 },
  pylon: { color: '#6aa8ff', r: 3.2, k: 0.42 },
  crystal_ball: { color: '#9fffd6', r: 2.2, k: 0.4 },
  orrery: { color: '#ffd98a', r: 1.6, k: 0.35 },
  fountain: { color: '#f26bbd', r: 3.4, k: 0.35 },
  summoning: { color: '#ff5a3c', r: 3.6, k: 0.35 },
  alchemy: { color: '#ffcf4a', r: 2.6, k: 0.3, dz: 0.3 },
  pipes: { color: '#f26bbd', r: 1.3, k: 0.3, dz: 0.4 },
};

function lightPools(root: THREE.Object3D, list: Placement[]): void {
  const pos: number[] = [];
  const col: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const c = new THREE.Color();
  for (const pl of list) {
    const pool = POOLS[pl.p];
    if (!pool) continue;
    c.set(pl.p === 'crystals' ? (pl.tint?.glow ?? pool.color) : pool.color).multiplyScalar(pool.k);
    // Offset forward along the prop's facing (wall lamps light the floor in front of them).
    const a = THREE.MathUtils.degToRad(pl.rot ?? 0);
    const x = pl.x + Math.sin(a) * (pool.dz ?? 0);
    const z = pl.z + Math.cos(a) * (pool.dz ?? 0);
    const y = (isRoomProp(pl) ? ROOM_FLOOR_Y : 0.012) + 0.03;
    const r = pool.r * (pl.s ?? 1);
    const base = pos.length / 3;
    for (const [dx, dz, u, v] of [
      [-r, -r, 0, 0],
      [r, -r, 1, 0],
      [r, r, 1, 1],
      [-r, r, 0, 1],
    ] as const) {
      pos.push(x + dx, y, z + dz);
      col.push(c.r, c.g, c.b);
      uv.push(u, v);
    }
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  const mesh = new THREE.Mesh(
    g,
    new THREE.MeshBasicMaterial({ map: glowPool(), vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }),
  );
  mesh.name = 'light-pools';
  mesh.userData.dynamic = true; // additive: keep out of the opaque batches
  mesh.renderOrder = 2;
  root.add(mesh);
}

/** Things on the table beside the board, at tabletop scale: the desk the Ministry sits on. */
function tableDressing(): Placement[] {
  const y = TABLE_Y - 0.012;
  return [
    // (The Decrees sit north of the spell display; the desk things keep clear of them.)
    { p: 'inkwell', x: -39.2, z: 1.4, s: 3.2, rot: 25, y },
    { p: 'scroll', x: -39.6, z: 7.6, s: 2.8, rot: 18, y },
    { p: 'coin_dish', x: 32.4, z: -14.6, s: 2.3, y },
  ];
}

function furnish(root: THREE.Object3D, lib: PropLibrary): void {
  const group = new THREE.Group();
  group.name = 'props';
  const list = [...placements(), ...garden(), ...tableDressing()];
  lightPools(root, list);
  for (const pl of list) {
    const o = lib.make(pl.p, pl.tint);
    o.position.set(pl.x, (pl.y ?? 0) + (isRoomProp(pl) ? ROOM_FLOOR_Y : 0.012), pl.z);
    o.rotation.y = THREE.MathUtils.degToRad(pl.rot ?? 0);
    if (pl.s) o.scale.setScalar(pl.s);
    group.add(o);
  }
  root.add(group);
}

function isRoomProp(pl: Placement): boolean {
  return pl.x > BUILDING.x[0] && pl.x < BUILDING.x[1] && pl.z > BUILDING.z[0] && pl.z < BUILDING.z[1];
}

export type { RoomLayout };
